/**
 * TanStack Query hooks for `affiliate-approval`. Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	approve,
	fetchQueue,
	reject,
	affiliateApprovalQueryKeys,
	type ApproveResponse,
	type ListAffiliateApprovalResponse,
	type ListQuery,
	type RejectResponse,
} from "./api";

export function useAffiliateApprovalQueue(
	query: ListQuery,
): UseQueryResult<ListAffiliateApprovalResponse, Error> {
	return useQuery({
		queryKey: affiliateApprovalQueryKeys.queue(query),
		queryFn: () => fetchQueue(query),
	});
}

export function useAffiliateApprovalApprove(): UseMutationResult<
	ApproveResponse,
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: approve,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: affiliateApprovalQueryKeys.all });
		},
	});
}

export function useAffiliateApprovalReject(): UseMutationResult<
	RejectResponse,
	Error,
	{ id: string; reason: string }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: reject,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: affiliateApprovalQueryKeys.all });
		},
	});
}

/* Back-compat aliases for the scaffold shape. */
export function useAffiliateApprovalList(): UseQueryResult<
	ListAffiliateApprovalResponse,
	Error
> {
	return useAffiliateApprovalQueue({ status: "pending" });
}
